import React, { useState, useMemo, useEffect } from "react";
import { useAppContext } from "../Context/context";
import { getSecureItem, setSecureItem } from "../services/SecureStorage";

function ColumnSelector({ columnsData, onSelectionChange, onSave }) {
  
  // Load saved columns from localStorage on component mount
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [columnOrder, setColumnOrder] = useState([]); // Array to maintain order
  const [draggedIndex, setDraggedIndex] = useState(null);

  const {
    setSelectedColumns: setContextSelectedColumns,
    setSelectedColumnsData: setContextSelectedColumnsData,
  } = useAppContext();

  // Load saved columns from localStorage when component mounts
  useEffect(() => {
    try {
      const saved = getSecureItem('selectedPayrollColumns');
      const savedOrder = getSecureItem('selectedPayrollColumnsOrder');
      
      if (saved) {
        const savedSet = new Set(JSON.parse(saved));
        setSelectedColumns(savedSet);
        
        // Load order if available, otherwise use saved columns array
        if (savedOrder) {
          const uniqueOrder = Array.from(new Set(JSON.parse(savedOrder)));
          setColumnOrder(uniqueOrder);
        } else {
          setColumnOrder(Array.from(savedSet));
        }
        
        // Trigger onSelectionChange with saved data
        if (onSelectionChange && columnsData?.length) {
          onSelectionChange(getSelectedColumnsData(savedSet));
        }
      }
    } catch (error) {
      console.error('Error loading saved columns:', error);
    }
  }, []); // Empty dependency array - only run on mount

  // Transform original payroll data to match desired structure
  const transformPayrollForSelectedColumns = (originalData, selectedHeaders) => {
    if (!Array.isArray(originalData)) return [];

    const parseArray = (strOrArr) => {
      if (!strOrArr) return [];
      try {
        return typeof strOrArr === "string" ? JSON.parse(strOrArr) : strOrArr;
      } catch {
        return [];
      }
    };

    return originalData.map(employee => {
      const result = { ...employee };
      
      const additions = [];
      const deductions = [];

      parseArray(employee.Additions).forEach(item => {
        if (selectedHeaders.has(item.ColumnHeaderName?.trim())) {
          additions.push(item);
        }
      });

      parseArray(employee.Deductions).forEach(item => {
        if (selectedHeaders.has(item.ColumnHeaderName?.trim())) {
          if (item.ComponentType === 'Addition') {
            additions.push(item);
          } else {
            deductions.push(item);
          }
        }
      });

      result.Additions = additions;
      result.Deductions = deductions;

      return result;
    });
  };

  // Extract unique columns from all employees
  const uniqueColumns = useMemo(() => {
    if (!Array.isArray(columnsData) || columnsData.length === 0) return [];

    const columnMap = new Map();

    const parseArray = (strOrArr) => {
      if (!strOrArr) return [];
      try {
        return typeof strOrArr === "string" ? JSON.parse(strOrArr) : strOrArr;
      } catch (e) {
        console.warn("Parse error:", e);
        return [];
      }
    };

    columnsData.forEach((employee) => {
      const additions = parseArray(employee.Additions);
      const deductions = parseArray(employee.Deductions);

      [...additions, ...deductions].forEach((item) => {
        const header = item.ColumnHeaderName?.trim();
        if (header && !columnMap.has(header)) {
          columnMap.set(header, {
            ColumnHeader: header,
            ComponentType: item.ComponentType,
            ColumnOrder: columnMap.size,
            ColumnWidth: header.length > 12 ? 180 : 140,
            WageTypeNames: item.WageTypeName || null,
          });
        }
      });
    });

    return Array.from(columnMap.values());
  }, [columnsData]);

  const groupedColumns = useMemo(() => {
    const groups = { Addition: [], Deduction: [] };
    uniqueColumns.forEach((col) => {
      groups[col.ComponentType === "Addition" ? "Addition" : "Deduction"].push(col);
    });
    return groups;
  }, [uniqueColumns]);

  const getSelectedColumnsData = (selectedSet) => {
    if (selectedSet.size === 0) return [];

    const parseArray = (strOrArr) => {
      if (!strOrArr) return [];
      try {
        return typeof strOrArr === "string" ? JSON.parse(strOrArr) : strOrArr;
      } catch {
        return [];
      }
    };

    // Use columnOrder array to maintain order
    const orderedColumns = columnOrder
      .filter(header => selectedSet.has(header))
      .map(header => uniqueColumns.find(col => col.ColumnHeader === header))
      .filter(Boolean);

    return orderedColumns.map((col) => {
      const items = columnsData.map((employee) => {
        const components =
          col.ComponentType === "Addition"
            ? parseArray(employee.Additions)
            : parseArray(employee.Deductions);

        const match = components.find(
          (c) => c.ColumnHeaderName?.trim() === col.ColumnHeader
        );

        return {
          empId: employee.EmpId,
          name: employee.Name,
          code: employee.Code || "",
          department: employee.DepartmentName || "",
          calculatedAmount: match ? Number(match.CalculatedAmount || 0) : 0,
          baseAmount: match ? Number(match.BaseAmount || 0) : 0,
          componentId: match?.ComponentId || null,
          componentName: match?.ComponentName || null,
          wageTypeName: match?.WageTypeName || null,
        };
      });

      return {
        columnHeader: col.ColumnHeader,
        componentType: col.ComponentType,
        columnOrder: col.ColumnOrder,
        columnWidth: col.ColumnWidth,
        items,
        summary: {
          totalEmployees: items.length,
          nonZeroCount: items.filter((i) => i.calculatedAmount > 0).length,
          totalAmount: items.reduce((sum, i) => sum + i.calculatedAmount, 0),
        },
      };
    });
  };

  const toggleColumn = (header) => {
    // First, determine if we're adding or removing
    const isCurrentlySelected = selectedColumns.has(header);
    
    // Update selectedColumns
    setSelectedColumns((prev) => {
      const newSet = new Set(prev);
      if (isCurrentlySelected) {
        newSet.delete(header);
      } else {
        newSet.add(header);
      }
      return newSet;
    });
    
    // Update columnOrder based on whether we're adding or removing
    setColumnOrder((prevOrder) => {
      if (isCurrentlySelected) {
        // Column is being REMOVED - filter it out
        return prevOrder.filter(col => col !== header);
      } else {
        // Column is being ADDED - add to end only if not already there
        if (!prevOrder.includes(header)) {
          return [...prevOrder, header];
        }
        return prevOrder;
      }
    });
  };

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    // Work with the filtered array
    const filteredOrder = Array.from(new Set(columnOrder)).filter(header => selectedColumns.has(header));
    const newOrder = [...filteredOrder];
    const draggedItem = newOrder[draggedIndex];
    
    // Remove dragged item
    newOrder.splice(draggedIndex, 1);
    // Insert at new position
    newOrder.splice(index, 0, draggedItem);
    
    // Update the main columnOrder with the new order
    setColumnOrder(newOrder);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    // Clean up columnOrder - remove duplicates and save
    const cleanOrder = Array.from(new Set(columnOrder)).filter(header => selectedColumns.has(header));
    setColumnOrder(cleanOrder);
    
    // Save to localStorage immediately after reordering
    try {
      setSecureItem('selectedPayrollColumnsOrder', JSON.stringify(cleanOrder));
    } catch (error) {
      console.error('Error saving order:', error);
    }
  };

  // Updated handleSave with localStorage persistence
  const handleSave = () => {
    // ✅ Use columnOrder as the selectedHeaders (this maintains the order)
    const orderedHeaders = columnOrder.filter(header => selectedColumns.has(header));
    const transformedData = transformPayrollForSelectedColumns(columnsData, selectedColumns);
    
    // ✅ Save to localStorage (selected headers in order)
    try {
      setSecureItem('selectedPayrollColumns', JSON.stringify(orderedHeaders));
      setSecureItem('selectedPayrollColumnsOrder', JSON.stringify(orderedHeaders));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
    
    // Save to context: selectedHeaders array is now in the correct order
    setTimeout(() => {
      setContextSelectedColumns(selectedColumns);
      setContextSelectedColumnsData({
        selectedHeaders: orderedHeaders, // ✅ This array is now in display order
        transformedPayrollData: transformedData,
      });
    }, 0);

    if (onSave) {
      onSave({
        selectedHeaders: orderedHeaders, // ✅ Array in display order
        transformedPayrollData: transformedData,
      });
    }
  };

  const selectAllInGroup = (type) => {
    const columnsToAdd = groupedColumns[type]
      .map(col => col.ColumnHeader)
      .filter(header => !selectedColumns.has(header));
    
    if (columnsToAdd.length === 0) return;
    
    // Update selectedColumns
    setSelectedColumns((prev) => {
      const newSet = new Set(prev);
      columnsToAdd.forEach(header => newSet.add(header));
      return newSet;
    });
    
    // Update columnOrder - add only new columns that aren't already in order
    setColumnOrder((prevOrder) => {
      const newColumns = columnsToAdd.filter(header => !prevOrder.includes(header));
      if (newColumns.length > 0) {
        return [...prevOrder, ...newColumns];
      }
      return prevOrder;
    });
  };

  const deselectAllInGroup = (type) => {
    const columnsToRemove = groupedColumns[type]
      .map(col => col.ColumnHeader)
      .filter(header => selectedColumns.has(header));
    
    if (columnsToRemove.length === 0) return;
    
    // Update selectedColumns
    setSelectedColumns((prev) => {
      const newSet = new Set(prev);
      columnsToRemove.forEach(header => newSet.delete(header));
      return newSet;
    });
    
    // Update columnOrder - remove the deselected columns
    setColumnOrder((prevOrder) => {
      return prevOrder.filter(col => !columnsToRemove.includes(col));
    });
  };

  const renderColumnGroup = (type, columns) => {
    if (columns.length === 0) return null;

    return (
      <div className="mb-12">
        <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-300">
          <h3 className="text-2xl font-bold text-gray-800">{type}s</h3>
          <div className="flex gap-4">
            <button
              onClick={() => selectAllInGroup(type)}
              className="px-5 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
            >
              Select All
            </button>
            <button
              onClick={() => deselectAllInGroup(type)}
              className="px-5 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Clear All
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {columns.map((col) => (
            <label
              key={col.ColumnHeader}
              className="flex items-center gap-4 p-5 rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
            >
              <input
                type="checkbox"
                checked={selectedColumns.has(col.ColumnHeader)}
                onChange={() => toggleColumn(col.ColumnHeader)}
                className="w-6 h-6 text-blue-600 rounded focus:ring-blue-300"
              />
              <div>
                <div className="text-lg font-semibold text-gray-900">
                  {col.ColumnHeader}
                </div>
                {col.WageTypeNames && (
                  <div className="text-sm text-gray-500 mt-1">
                    {col.WageTypeNames}
                  </div>
                )}
              </div>
            </label>
          ))}
        </div>
      </div>
    );
  };

  if (!columnsData?.length) {
    return <div className="p-10 text-center text-gray-500">No payroll data available</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800">Select Payroll Columns</h2>
              <p className="text-xl text-gray-600 mt-3">
                {selectedColumns.size} column{selectedColumns.size !== 1 ? "s" : ""} selected
                {selectedColumns.size > 0 && (
                  <span className="ml-2 text-green-600 font-medium">(Saved)</span>
                )}
              </p>
            </div>
            <button
              onClick={handleSave}
              disabled={selectedColumns.size === 0}
              className={`px-10 py-5 rounded-xl text-xl font-bold transition-all ${
                selectedColumns.size === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700 shadow-xl hover:shadow-2xl"
              }`}
            >
              Save Selection ({selectedColumns.size})
            </button>
          </div>
        </div>

        {/* ✅ DRAGGABLE SELECTED COLUMNS ORDER DISPLAY */}
        {columnOrder.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Column Display Order</h3>
              <p className="text-sm text-gray-600">
                Drag and drop to reorder columns. This order will be used in reports and exports.
              </p>
            </div>
            
            <div className="space-y-3">
              {/* ✅ Filter out duplicates and only show selected columns */}
              {Array.from(new Set(columnOrder))
                .filter(header => selectedColumns.has(header))
                .map((header, index) => {
                const columnInfo = uniqueColumns.find(col => col.ColumnHeader === header);
                const isAddition = columnInfo?.ComponentType === "Addition";
                
                return (
                  <div
                    key={header}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-move transition-all ${
                      draggedIndex === index
                        ? ' shadow-lg scale-105'
                        : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    
                    {/* Order Number */}
                    <div className="flex-shrink-0 w-10 h-10 rounded-full  flex items-center justify-center">
                      <span className="text-lg font-bold text-blue-600">{index + 1}</span>
                    </div>
                    
                    {/* Column Info */}
                    <div className="flex-grow">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-gray-900">{header}</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          isAddition 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {isAddition ? 'Addition' : 'Deduction'}
                        </span>
                      </div>
                      {columnInfo?.WageTypeNames && (
                        <div className="text-sm text-gray-500 mt-1">
                          {columnInfo.WageTypeNames}
                        </div>
                      )}
                    </div>
                   
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg p-10">
          {groupedColumns.Addition.length > 0 && renderColumnGroup("Addition", groupedColumns.Addition)}
          {groupedColumns.Deduction.length > 0 && renderColumnGroup("Deduction", groupedColumns.Deduction)}
        </div>
      </div>
    </div>
  );
}

export default ColumnSelector;